import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Header from "../components/Header";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  MapPin, Briefcase, ArrowLeft, CheckCircle2, UserPlus, LogIn, Sparkles,
  ThumbsUp, AlertTriangle, Info, Loader2,
} from "lucide-react";
import ShareButton from "../components/ShareButton";
import ApplyResumeChoiceDialog from "../components/ApplyResumeChoiceDialog";
import useDocumentTitle from "../hooks/useDocumentTitle";

export default function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(false);
  const [authPrompt, setAuthPrompt] = useState(false);
  const [gapOpen, setGapOpen] = useState(false);
  const [gapLoading, setGapLoading] = useState(false);
  const [gap, setGap] = useState(null);
  const [nudgeOpen, setNudgeOpen] = useState(false);
  const [resumeChoiceOpen, setResumeChoiceOpen] = useState(false);
  useDocumentTitle(job?.title || "Job");

  useEffect(() => {
    api.get(`/jobs/${id}`).then(({ data }) => {
      setJob(data);
      if (data?.applied) setApplied(true);
    }).finally(() => setLoading(false));
    if (user?.role === "job_seeker") {
      api.get("/applications/mine").then(({ data }) => {
        if ((data || []).some(a => a.job_id === id)) setApplied(true);
      }).catch(()=>{});
    }
  }, [id, user]);

  // One-click Apply from external CTAs (top-match email, embed badge)
  // If ?apply=1 is present and user is a verified seeker who hasn't applied yet, auto-fire the apply flow.
  useEffect(() => {
    if (loading || !job) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("apply") !== "1") return;
    if (applied || applying) return;
    if (!user) { setAuthPrompt(true); return; }
    if (user.role !== "job_seeker") return;
    // Fast-apply link from an external CTA — open the resume-choice dialog
    // directly (skip the pre-apply nudge since the intent is explicit).
    setResumeChoiceOpen(true);
    // strip the query so a page reload doesn't re-trigger
    const url = new URL(window.location.href);
    url.searchParams.delete("apply");
    window.history.replaceState({}, "", url.toString());
  }, [loading, job, user]);

  // Opens the resume-choice dialog. The dialog itself submits to /api/jobs/{id}/apply.
  const openResumeChoice = () => {
    if (!user) { setAuthPrompt(true); return; }
    if (user.role !== "job_seeker") { toast.error("Only job seekers can apply"); return; }
    if (applied || applying) return;
    setResumeChoiceOpen(true);
  };

  const onApplicationSubmitted = () => {
    setApplied(true);
  };

  const runPreApplyCheck = async () => {
    if (!user) { setAuthPrompt(true); return; }
    if (user.role !== "job_seeker") { toast.error("Only job seekers can check fit"); return; }
    setGapOpen(true);
    setGap(null);
    setGapLoading(true);
    try {
      const { data } = await api.get(`/ai/pre-apply-check/${id}`);
      setGap(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to run fit check");
      setGapOpen(false);
    } finally {
      setGapLoading(false);
    }
  };

  const applyFromGap = () => {
    setGapOpen(false);
    setTimeout(() => openResumeChoice(), 120);
  };

  // "Apply now" click intercept — show the pre-apply nudge (once, until they act).
  const onApplyClick = () => {
    if (!user) { setAuthPrompt(true); return; }
    if (user.role !== "job_seeker") { toast.error("Only job seekers can apply"); return; }
    if (applied || applying) return;
    setNudgeOpen(true);
  };

  const nudgeAnalyzeFit = () => {
    setNudgeOpen(false);
    setTimeout(() => runPreApplyCheck(), 120);
  };

  const nudgeSkipAndApply = () => {
    setNudgeOpen(false);
    setTimeout(() => openResumeChoice(), 120);
  };

  const goSignup = () => {
    const returnTo = `/jobs/${id}`;
    navigate(`/signup?role=job_seeker&return_to=${encodeURIComponent(returnTo)}`);
  };
  const goLogin = () => {
    const returnTo = `/jobs/${id}`;
    navigate(`/login?return_to=${encodeURIComponent(returnTo)}`);
  };

  if (loading) return <div className="min-h-screen"><Header /><div className="p-10 text-sm text-zinc-500">Loading…</div></div>;
  if (!job) return <div className="min-h-screen"><Header /><div className="p-10">Not found</div></div>;

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Link to="/jobs" className="inline-flex items-center text-sm text-zinc-500 hover:text-zinc-900 mb-6">
          <ArrowLeft className="h-4 w-4 mr-1" /> All jobs
        </Link>

        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
          <div>
            <div className="text-xs uppercase tracking-widest text-[#002FA7] font-bold mb-1">{job.company_name}</div>
            <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter text-zinc-950">{job.title}</h1>
            <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-zinc-500 tag-mono">
              <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {job.location}</span>
              <span>·</span>
              <span className="flex items-center gap-1"><Briefcase className="h-3.5 w-3.5" /> {job.job_type}</span>
              <span>·</span>
              <span>{job.experience_min}–{job.experience_max} yrs</span>
              {job.salary_range ? (
                <><span>·</span><span data-testid="job-salary-display">{job.salary_range}</span></>
              ) : (
                <><span>·</span><span data-testid="job-salary-hidden" className="text-zinc-400 italic">Salary undisclosed</span></>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
          {applied && (
            <span
              className="inline-flex items-center gap-1.5 px-3 h-11 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 tag-mono text-xs font-bold"
              data-testid="applied-checkmark-badge"
            >
              <CheckCircle2 className="h-4 w-4" /> APPLIED
            </span>
          )}
          {user?.role === "job_seeker" && !applied && (
            <Button
              size="lg"
              variant="outline"
              onClick={runPreApplyCheck}
              data-testid="check-fit-btn"
              className="h-11 border-blue-200 text-[#002FA7] hover:bg-blue-50"
            >
              <Sparkles className="h-4 w-4 mr-2" /> Check fit
            </Button>
          )}
          <Button
            size="lg"
            onClick={onApplyClick}
            disabled={applying || applied}
            data-testid="apply-job-btn"
            className="bg-[#002FA7] hover:bg-[#00227A] text-white h-11 disabled:opacity-70"
          >
            {applied ? <><CheckCircle2 className="h-4 w-4 mr-2" /> Applied</> : applying ? "Applying…" : "Apply now"}
          </Button>
          <ShareButton
            url={`/jobs/${job.job_id}`}
            title={`${job.title} at ${job.company_name} — apply on QaTestology`}
            testId="job-share-btn"
            label="Share"
          />
          </div>
        </div>

        <Card className="p-6 mb-6">
          <h2 className="font-display text-lg font-bold tracking-tight mb-3">About the role</h2>
          <p className="text-zinc-700 whitespace-pre-line leading-relaxed">{job.description}</p>
        </Card>

        <div className="grid md:grid-cols-2 gap-6">
          <Card className="p-6">
            <h2 className="font-display text-lg font-bold tracking-tight mb-3">Required skills</h2>
            <div className="flex flex-wrap gap-1.5">
              {(job.required_skills || []).map(s => (
                <span key={s} className="tag-mono px-2 py-1 bg-blue-50 text-blue-700 border border-blue-200 rounded">{s}</span>
              ))}
              {(job.required_skills || []).length === 0 && <span className="text-sm text-zinc-400">No specific skills listed.</span>}
            </div>
          </Card>
          <Card className="p-6">
            <h2 className="font-display text-lg font-bold tracking-tight mb-3">Nice to have</h2>
            <div className="flex flex-wrap gap-1.5">
              {(job.nice_to_have || []).map(s => (
                <Badge key={s} variant="outline" className="tag-mono">{s}</Badge>
              ))}
              {(job.nice_to_have || []).length === 0 && <span className="text-sm text-zinc-400">—</span>}
            </div>
          </Card>
        </div>
      </main>

      <Dialog open={nudgeOpen} onOpenChange={setNudgeOpen}>
        <DialogContent className="max-w-md" data-testid="apply-nudge-dialog">
          <DialogHeader>
            <div className="mx-auto h-12 w-12 rounded-full bg-blue-50 grid place-items-center text-[#002FA7] mb-2">
              <Sparkles className="h-6 w-6" />
            </div>
            <DialogTitle className="text-center font-display text-2xl font-black tracking-tight">
              Analyse your fit first?
            </DialogTitle>
            <DialogDescription className="text-center text-zinc-600 pt-1">
              Before applying, we can run a quick <span className="font-semibold text-zinc-900">skill-match analysis</span> against{" "}
              <span className="font-semibold text-zinc-900">{job.title}</span> so you know where you stand.
              <br />
              <span className="text-xs text-zinc-500 tag-mono mt-1 inline-block">Optional — you can skip and apply directly.</span>
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col sm:flex-row-reverse gap-2 mt-2">
            <Button
              onClick={nudgeAnalyzeFit}
              data-testid="nudge-analyze-btn"
              className="bg-[#002FA7] hover:bg-[#00227A] text-white flex-1"
            >
              <Sparkles className="h-4 w-4 mr-2" /> Analyse fit
            </Button>
            <Button
              variant="outline"
              onClick={nudgeSkipAndApply}
              disabled={applying}
              data-testid="nudge-skip-apply-btn"
              className="flex-1"
            >
              Skip &amp; apply directly
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={gapOpen} onOpenChange={setGapOpen}>
        <DialogContent className="max-w-lg" data-testid="pre-apply-dialog">
          <DialogHeader>
            <div className="mx-auto h-12 w-12 rounded-full bg-blue-50 grid place-items-center text-[#002FA7] mb-2">
              <Sparkles className="h-6 w-6" />
            </div>
            <DialogTitle className="text-center font-display text-2xl font-black tracking-tight">
              How well do you fit?
            </DialogTitle>
            <DialogDescription className="text-center text-zinc-600 pt-1">
              We compared your profile skills against{" "}
              <span className="font-semibold text-zinc-900">{job.title}</span>.
            </DialogDescription>
          </DialogHeader>

          {gapLoading || !gap ? (
            <div className="py-8 flex flex-col items-center justify-center text-sm text-zinc-500 gap-2">
              <Loader2 className="h-5 w-5 animate-spin text-[#002FA7]" />
              <span>Analysing your fit…</span>
            </div>
          ) : (
            <div className="space-y-4">
              <FitVerdictHeader gap={gap} />

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs uppercase tracking-widest text-zinc-500 tag-mono">match score</span>
                  <span className="font-display text-lg font-black tracking-tighter text-zinc-950" data-testid="pre-apply-score">
                    {gap.match_score}%
                  </span>
                </div>
                <Progress value={gap.match_score} className="h-2" />
              </div>

              {gap.matched_skills?.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-widest text-emerald-700 font-bold mb-1.5">you already have</div>
                  <div className="flex flex-wrap gap-1.5">
                    {gap.matched_skills.map((s) => (
                      <span key={`m-${s}`} className="tag-mono px-2 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded text-[11px]">✓ {s}</span>
                    ))}
                  </div>
                </div>
              )}

              {gap.missing_skills?.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-widest text-amber-700 font-bold mb-1.5">gaps to address</div>
                  <div className="flex flex-wrap gap-1.5">
                    {gap.missing_skills.map((s) => (
                      <span key={`g-${s}`} className="tag-mono px-2 py-0.5 bg-amber-50 text-amber-800 border border-amber-200 rounded text-[11px]">⚠ {s}</span>
                    ))}
                  </div>
                </div>
              )}

              {gap.prep_suggestions?.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-widest text-[#002FA7] font-bold mb-1.5">prep suggestions</div>
                  <ul className="space-y-1 text-sm text-zinc-700 list-disc list-inside">
                    {gap.prep_suggestions.map((p, i) => (
                      <li key={`p-${i}-${String(p).slice(0, 30)}`}>{p}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="flex flex-col sm:flex-row sm:justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setGapOpen(false)} data-testid="pre-apply-close">
              Close
            </Button>
            {!applied && (
              <Button
                onClick={applyFromGap}
                disabled={applying}
                data-testid="pre-apply-apply-btn"
                className="bg-[#002FA7] hover:bg-[#00227A] text-white"
              >
                Continue to apply
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ApplyResumeChoiceDialog
        open={resumeChoiceOpen}
        onOpenChange={setResumeChoiceOpen}
        jobId={id}
        jobTitle={job.title}
        onApplied={onApplicationSubmitted}
      />

      <Dialog open={authPrompt} onOpenChange={setAuthPrompt}>
        <DialogContent data-testid="apply-auth-dialog" className="max-w-md">
          <DialogHeader>
            <div className="mx-auto h-12 w-12 rounded-full bg-blue-50 grid place-items-center text-[#002FA7] mb-2">
              <Sparkles className="h-6 w-6" />
            </div>
            <DialogTitle className="text-center font-display text-2xl font-black tracking-tight">
              One quick step to apply
            </DialogTitle>
            <DialogDescription className="text-center text-zinc-600 pt-1">
              To apply for <span className="font-semibold text-zinc-900">{job.title}</span>, you need a
              QaTestology tester profile. It's free and takes under a minute.{" "}
              <button
                type="button"
                onClick={goSignup}
                data-testid="apply-auth-inline-register"
                className="font-semibold text-[#002FA7] hover:text-[#00227A] underline underline-offset-2"
              >
                Register now
              </button>
              {" "}or sign in below.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-center gap-2 mt-2">
            <Button
              variant="outline"
              onClick={goLogin}
              data-testid="apply-auth-login-btn"
              className="flex-1 min-w-0 border-zinc-300"
            >
              <LogIn className="h-4 w-4 mr-2 flex-shrink-0" />
              <span className="truncate">Sign in</span>
            </Button>
            <Button
              onClick={goSignup}
              data-testid="apply-auth-register-btn"
              className="bg-[#002FA7] hover:bg-[#00227A] text-white flex-1 min-w-0"
            >
              <UserPlus className="h-4 w-4 mr-2 flex-shrink-0" />
              <span className="truncate">Create tester profile</span>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FitVerdictHeader({ gap }) {
  const v = gap?.verdict;
  const map = {
    confidently_apply: {
      icon: ThumbsUp,
      label: "Confident",
      pill: "bg-emerald-50 text-emerald-700 border-emerald-200",
    },
    should_apply: {
      icon: Info,
      label: "Good to apply",
      pill: "bg-blue-50 text-[#002FA7] border-blue-200",
    },
    think_before: {
      icon: AlertTriangle,
      label: "Needs more preparation",
      pill: "bg-amber-50 text-amber-800 border-amber-200",
    },
  };
  const cfg = map[v] || map.should_apply;
  const Icon = cfg.icon;
  return (
    <div className="flex items-start gap-3">
      <span className={`h-10 w-10 rounded-md grid place-items-center border ${cfg.pill}`}>
        <Icon className="h-5 w-5" />
      </span>
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-display text-lg font-black tracking-tight text-zinc-950">{gap?.headline}</span>
          <span className={`tag-mono text-[10px] px-1.5 py-0.5 rounded-full border ${cfg.pill}`} data-testid="pre-apply-verdict">
            {cfg.label}
          </span>
        </div>
        <p className="text-sm text-zinc-700 mt-1">{gap?.message}</p>
      </div>
    </div>
  );
}
